import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';
import { ArrowRight, CheckCircle2, Shield, Zap, Clock } from 'lucide-react';

const formSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email address").max(255),
  company: z.string().trim().min(1, "Company is required").max(100),
  message: z.string().trim().max(1000).optional().or(z.literal('')),
});

export const DemoContactForm = () => {
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const validation = formSchema.safeParse(formData);
      if (!validation.success) {
        toast({ title: "Validation error", description: validation.error.errors[0].message, variant: "destructive" });
        setIsSubmitting(false);
        return;
      }

      const { error } = await supabase.from('waitlist').insert([{
        name: validation.data.name,
        email: validation.data.email,
        company: validation.data.company,
        message: `[DEMO REQUEST] ${validation.data.message || 'Requested a demo'}`,
      }]);

      if (error) {
        if (error.code === '23505') {
          toast({ title: "Already received!", description: "We've already got your request. We'll be in touch soon." });
        } else {
          toast({ title: "Something went wrong", description: "Please try again later.", variant: "destructive" });
        }
      } else {
        toast({ title: "Demo request received! 🎉", description: "We'll reach out within 24 hours to schedule your demo." });
        setFormData({ name: '', email: '', company: '', message: '' });
      }
    } catch {
      toast({ title: "Something went wrong", description: "Please try again later.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <section id="book-demo" className="w-full py-20 md:py-32 bg-foreground relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="container relative z-10 px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
          {/* Left: copy */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-background mb-6">
              Stop managing your CRM.{' '}
              <span className="text-primary">Start closing deals.</span>
            </h2>
            <p className="text-lg text-background/70 mb-10 leading-relaxed">
              See how NoQ5 can transform your revenue operations in a 20-minute personalised demo.
            </p>

            <div className="space-y-4 mb-8">
              {[
                { icon: Clock, text: "7-day free trial — no credit card required" },
                { icon: Zap, text: "5-minute setup with HubSpot + Google Workspace" },
                { icon: Shield, text: "EU-based, GDPR compliant, enterprise-ready" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-background/80">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            <p className="text-background/40 text-sm italic">
              "Our forecast accuracy improved 40% in the first month." — Sales Director, Beta
            </p>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <div className="bg-card rounded-3xl p-8 border border-border shadow-card">
              <h3 className="text-xl font-bold text-foreground mb-6">Book a Demo</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    name="name"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    maxLength={100}
                    className="h-12 rounded-xl"
                  />
                  <Input
                    name="email"
                    type="email"
                    placeholder="Work email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    maxLength={255}
                    className="h-12 rounded-xl"
                  />
                </div>
                <Input
                  name="company"
                  placeholder="Company name"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  maxLength={100}
                  className="h-12 rounded-xl"
                />
                <Textarea
                  name="message"
                  placeholder="Anything you'd like us to cover? (optional)"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={1000}
                  className="min-h-[80px] rounded-xl"
                />
                <Button
                  type="submit"
                  size="lg"
                  className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-xl shadow-button hover:shadow-glow transition-all duration-300 group"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Book a Demo'}
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </form>
              <p className="text-xs text-muted-foreground mt-4 text-center">
                We'll respond within 24 hours. No spam, ever.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
